import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { UserType } from "../../api/authService";
import adminTheme from "../Admin/theme";
import { brandColors } from "../LandingPage.theme";

export default function Login() {
  const { t } = useTranslation("boLogin");
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const highlights = t("highlights", { returnObjects: true });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    try {
      const data = await login(email, password);

      if (data.userType !== UserType.BusinessOwner) {
        setError(t("notAuthorized"));
        localStorage.removeItem("bigmarket_auth");
        localStorage.removeItem("bigmarket_token");
        window.location.reload();
        return;
      }

      navigate("/business-owner", { replace: true });
    } catch (err) {
      if (!err.response) {
        setError(t("serverUnreachable", { message: err.message }));
        return;
      }

      const message = err.response.data?.title || err.response.data;
      setError(typeof message === "string" ? message : t("loginFailed"));
    }
  }

  return (
    <ThemeProvider theme={adminTheme}>
      <CssBaseline />
      {/* This console's layout intentionally stays LTR even when Arabic/Hebrew is the
          active site language - only the text is translated (see BusinessOwnerLayout.js). */}
      <Box
        dir="ltr"
        sx={{
          minHeight: "100vh",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        {/* Form panel */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.default",
            px: 3,
            py: 6,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 400,
              p: { xs: 3, sm: 5 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4, display: { xs: "flex", md: "none" } }}>
              <Box
                component="img"
                src="/apple-touch-icon.png"
                alt="Salatty"
                sx={{ width: 36, height: 36, borderRadius: "10px" }}
              />
              <Typography variant="h6" fontWeight={800}>
                Salatty
              </Typography>
            </Stack>

            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              {t("welcomeBack")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {t("signInSubtitle")}
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                {error && <Alert severity="error">{error}</Alert>}

                <TextField
                  label={t("email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                  autoComplete="email"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <TextField
                  label={t("password")}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  fullWidth
                  autoComplete="current-password"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOff fontSize="small" />
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />

                <Button type="submit" variant="contained" size="large" disabled={loading}>
                  {loading ? t("signingIn") : t("signIn")}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* Brand panel */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            position: "relative",
            overflow: "hidden",
            flexDirection: "column",
            justifyContent: "center",
            px: 8,
            py: 6,
            color: "#fff",
            background: `linear-gradient(135deg, ${brandColors.emeraldLight} 0%, ${brandColors.emeraldDark} 100%)`,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -70,
              right: -70,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -100,
              left: -60,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.06)",
            }}
          />

          <Box sx={{ position: "relative" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 6 }}>
              <Box
                component="img"
                src="/apple-touch-icon.png"
                alt="Salatty"
                sx={{ width: 40, height: 40, borderRadius: "12px" }}
              />
              <Typography variant="h6" fontWeight={800}>
                Salatty
              </Typography>
            </Stack>

            <Typography variant="h3" fontWeight={800} sx={{ fontSize: 36, mb: 2, maxWidth: 420 }}>
              {t("growHeadline")}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.85)", mb: 5, maxWidth: 420 }}>
              {t("growSubtitle")}
            </Typography>

            <Stack spacing={2}>
              {highlights.map((item) => (
                <Stack key={item} direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleIcon sx={{ color: brandColors.amberLight, fontSize: 20, mt: "2px" }} />
                  <Typography sx={{ color: "rgba(255,255,255,0.92)" }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
